import { Box, Container, IconButton, Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppBreadcrumbs from "./AppBreadcrumbs";
import { useNavigate } from "react-router-dom";

/**
 * Consistent page wrapper for all pages.
 *
 * Props:
 *   title       - Page heading (string)
 *   subtitle    - Optional subheading (string)
 *   breadcrumbs - Array of { label, to } for navigation trail
 *   actions     - React node(s) rendered top-right (e.g. "New X" button)
 *   children    - Page body content
 */
const PageLayout = ({ title, subtitle, breadcrumbs, actions, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const showBack = window.history.length > 1;

  return (
    <Box
      component="main"
      sx={{ minHeight: "100vh", backgroundColor: "background.default" }}
    >
      {/* Spacer that matches the fixed AppBar height */}
      <Toolbar />

      <Container
        maxWidth="xl"
        sx={{
          py: 3,
          px: { xs: 2, sm: 3, md: 4 },
          pb: isMobile ? 10 : 3,
        }}
      >
        {breadcrumbs && breadcrumbs.length > 0 && (
          <AppBreadcrumbs items={breadcrumbs} />
        )}

        {(title || actions) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {showBack && (
                <IconButton
                  onClick={() => navigate(-1)}
                  size="small"
                  aria-label="Go back"
                  sx={{ color: "text.secondary" }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Box>
                {title && (
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{ color: "primary.main", fontWeight: 600 }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>

            {actions && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {actions}
              </Box>
            )}
          </Box>
        )}

        {children}
      </Container>
    </Box>
  );
};

export default PageLayout;
